import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '~/components/ui/button.tsx'
import { cn } from '~/lib/utils.ts'

interface TechnicalErrorAlertProps {
  error: unknown
  onRetry?: () => void
  className?: string
}

/**
 * The one place a failed query or mutation becomes something a user reads.
 *
 * Two audiences, one component:
 *   - The Kassenwart gets a plain German sentence and a retry button. No stack, no jargon.
 *   - Whoever they call gets the technical detail, behind a disclosure.
 *
 * The detail is never hidden entirely: "something went wrong" with no way to see what is
 * how a support call becomes an archaeology session. It is also never shown by default.
 */
export function TechnicalErrorAlert({ error, onRetry, className }: TechnicalErrorAlertProps) {
  const { t } = useTranslation()
  const [detailsShown, setDetailsShown] = useState(false)

  return (
    <div
      role="alert"
      className={cn(
        'rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-medium text-foreground">{t('fehler.technischTitel')}</p>
          <p className="text-muted-foreground">{t('fehler.technischText')}</p>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {onRetry ? (
              <Button size="sm" variant="outline" onClick={onRetry}>
                <RefreshCwIcon aria-hidden />
                {t('fehler.erneutVersuchen')}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              aria-expanded={detailsShown}
              onClick={() => setDetailsShown((shown) => !shown)}
            >
              {detailsShown ? t('fehler.detailsVerbergen') : t('fehler.detailsAnzeigen')}
            </Button>
          </div>

          {detailsShown ? (
            <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
              {describeError(error)}
            </pre>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * Renders an unknown thrown value as text. `unknown` and not `Error` on purpose: a rejected
 * fetch, a tRPC error and a thrown string are all reachable here, and the component must not
 * itself throw while reporting a throw.
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`
  }
  if (typeof error === 'string') return error
  try {
    return JSON.stringify(error, null, 2)
  } catch {
    return String(error)
  }
}
